/* eslint-disable max-lines */
import {
  CONSERVATION_TYPES,
  IS_WORKING_TYPES,
  DEFECTIVE_IN_TYPES,
  SECTIONS,
  GROUP_BY_SECTION,
  INPUT_TYPES,
  STATUS,
  isSinkAndCeilingEnabled,
  isIntercomEnabled,
  NON_SAVEABLE_SECTIONS,
  isWorkingId,
  defectiveInId,
  commentId,
  conservationId,
  ASSESSMENT_V2_SECTIONS,
} from '../constants';

import * as v2 from '../v2/constants';
import { findAssessmentItem } from '../v2/utils';
import DraftAssessment from './DraftAssessment';
import PersistedAssessment from './PersistedAssessment';

const draft = new DraftAssessment({
  sectionGroups: v2.GROUP_BY_SECTION,
  sectionItems: v2.SECTION_ITEMS,
});

const persisted = new PersistedAssessment({ draft });

const DEPENDENCIES = Object.freeze({
  [SECTIONS.LIGHT]: SECTIONS.ELECTRICAL,
  [SECTIONS.REGISTER]: SECTIONS.HYDRAULIC,
  [SECTIONS.TOILET]: SECTIONS.HYDRAULIC,
  [SECTIONS.VALVE]: SECTIONS.HYDRAULIC,
  [SECTIONS.FAUCET]: SECTIONS.HYDRAULIC,
  [SECTIONS.SHOWER]: SECTIONS.HYDRAULIC,
});

const SECTIONS_WITH_HIDDEN_IS_WORKING = [
  SECTIONS.FUSEBOX,
  SECTIONS.SWITCH_SOCKET,
  ...(isSinkAndCeilingEnabled() ? [SECTIONS.SINK] : []),
  SECTIONS.SIPHON_TAIL,
  SECTIONS.WALL,
  ...(isSinkAndCeilingEnabled() ? [SECTIONS.CEILING] : []),
  SECTIONS.FLOOR,
  SECTIONS.STRUCTURE,
  SECTIONS.PROPERTY_ITEMS,

  ...(v2.isEnabled() ? Object.values(v2.SECTION) : []),
];

const SECTIONS_WITH_HIDDEN_DEFECTIVE_ROOMS = [
  SECTIONS.ELECTRICAL,
  SECTIONS.FUSEBOX,
  SECTIONS.SWITCH_SOCKET,
  SECTIONS.HYDRAULIC,
  SECTIONS.TOILET,
  SECTIONS.VALVE,
  ...(isSinkAndCeilingEnabled() ? [SECTIONS.SINK] : []),
  SECTIONS.SIPHON_TAIL,
  SECTIONS.SHOWER,
  SECTIONS.STRUCTURE,
  SECTIONS.WALL,
  ...(isSinkAndCeilingEnabled() ? [SECTIONS.CEILING] : []),
  SECTIONS.FLOOR,
  SECTIONS.PROPERTY_ITEMS,
  ...(isIntercomEnabled() ? [SECTIONS.INTERCOM] : []),
  SECTIONS.BATHROOM_STALL,
  SECTIONS.BATHROOM_CABINET,
  SECTIONS.BEDROOM_CLOSET,
  SECTIONS.KITCHEN_CABINET,

  ...(v2.isEnabled() ? Object.values(v2.SECTION) : []),
];

const SECTIONS_WITH_HIDDEN_CONSERVATION = [
  SECTIONS.ELECTRICAL,
  SECTIONS.HYDRAULIC,
  SECTIONS.STRUCTURE,
  SECTIONS.PROPERTY_ITEMS,

  ...(v2.isEnabled() ? Object.values(v2.SECTION) : []),
];

const IS_WORKING_TYPES_BY_ID = Object.freeze({
  [SECTIONS.ELECTRICAL]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.HYDRAULIC]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.TOILET]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.VALVE]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.FAUCET]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.WINDOW]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  ...(isIntercomEnabled()
    ? { [SECTIONS.INTERCOM]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO] }
    : {}),
  [SECTIONS.BATHROOM_STALL]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.BATHROOM_CABINET]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.BEDROOM_CLOSET]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
  [SECTIONS.KITCHEN_CABINET]: [IS_WORKING_TYPES.YES, IS_WORKING_TYPES.NO],
});

const DEFECTIVE_DEFECTIVE_IN_TYPES_BY_ID = Object.freeze({
  [SECTIONS.REGISTER]: [
    DEFECTIVE_IN_TYPES.BATHROOM,
    DEFECTIVE_IN_TYPES.SERVICE_AREA,
    DEFECTIVE_IN_TYPES.KITCHEN,
    DEFECTIVE_IN_TYPES.OTHER,
  ],
  [SECTIONS.FAUCET]: [
    DEFECTIVE_IN_TYPES.BATHROOM,
    DEFECTIVE_IN_TYPES.SERVICE_AREA,
    DEFECTIVE_IN_TYPES.KITCHEN,
    DEFECTIVE_IN_TYPES.OTHER,
  ],
});

const splitComment = (str) => str.split(/\s*,\s*/).filter(Boolean);

const buildSections = () => {
  const sections = {};

  Object.values(SECTIONS).forEach((id) => {
    if (id in v2.SECTION) {
      sections[id] = draft.createSection(id);

      return;
    }

    const section = {
      id,
      items: {},
    };

    const dependencyId = DEPENDENCIES[id];

    const working = {
      id: isWorkingId(id),
      sectionId: id,
      value: '',
      options: {
        type: INPUT_TYPES.CHIP,
        items: IS_WORKING_TYPES_BY_ID[id] || Object.values(IS_WORKING_TYPES),
      },
    };

    if (dependencyId) {
      working.visibleIf = {
        sectionId: dependencyId,
        itemId: isWorkingId(dependencyId),
        value: IS_WORKING_TYPES.YES,
      };
    }

    const defectiveIn = {
      id: defectiveInId(id),
      sectionId: id,
      value: [],
      options: {
        type: INPUT_TYPES.CHIP,
        items:
          DEFECTIVE_DEFECTIVE_IN_TYPES_BY_ID[id] ||
          Object.values(DEFECTIVE_IN_TYPES),
      },
      visibleIf: {
        sectionId: id,
        itemId: working.id,
        value: IS_WORKING_TYPES.NO,
      },
    };

    const comment = {
      id: commentId(id),
      sectionId: id,
      value: '',
      options: {
        type: INPUT_TYPES.INPUT,
      },
      visibleIf: {
        sectionId: id,
        itemId: defectiveIn.id,
        value: DEFECTIVE_IN_TYPES.OTHER,
      },
    };

    const conservation = {
      id: conservationId(id),
      sectionId: id,
      value: '',
      options: {
        type: INPUT_TYPES.RADIO,
        items: Object.values(CONSERVATION_TYPES),
      },
    };

    const showIsWorking = !SECTIONS_WITH_HIDDEN_IS_WORKING.includes(id);
    const showDefectiveIn = !SECTIONS_WITH_HIDDEN_DEFECTIVE_ROOMS.includes(id);
    const showConsevartion = !SECTIONS_WITH_HIDDEN_CONSERVATION.includes(id);

    if (showIsWorking) {
      section.items[working.id] = working;
    }

    if (showDefectiveIn) {
      section.items[defectiveIn.id] = defectiveIn;
      section.items[comment.id] = comment;
    }

    if (showConsevartion) {
      section.items[conservation.id] = conservation;
    }

    sections[section.id] = section;
  });

  return sections;
};

const buildDependencies = () => {
  let dependencies = {};

  Object.values(SECTIONS).forEach((id) => {
    if (id in v2.SECTION_ITEMS) {
      dependencies = {
        ...dependencies,
        ...draft.createDependencies(id),
      };

      return;
    }

    const dependencyId = DEPENDENCIES[id];

    const showDefectiveIn = !SECTIONS_WITH_HIDDEN_DEFECTIVE_ROOMS.includes(id);

    if (dependencyId) {
      dependencies[isWorkingId(dependencyId)] =
        dependencies[isWorkingId(dependencyId)] || [];

      dependencies[defectiveInId(dependencyId)] =
        dependencies[defectiveInId(dependencyId)] || [];

      dependencies[isWorkingId(dependencyId)].push({
        sectionId: id,
        itemId: isWorkingId(id),
      });

      if (showDefectiveIn) {
        dependencies[isWorkingId(dependencyId)].push(
          {
            sectionId: id,
            itemId: defectiveInId(id),
          },
          {
            sectionId: id,
            itemId: commentId(id),
          },
        );

        dependencies[defectiveInId(dependencyId)].push({
          sectionId: id,
          itemId: commentId(id),
        });
      }
    }

    dependencies[isWorkingId(id)] = dependencies[isWorkingId(id)] || [];
    dependencies[defectiveInId(id)] = dependencies[defectiveInId(id)] || [];

    if (showDefectiveIn) {
      dependencies[isWorkingId(id)].push(
        {
          sectionId: id,
          itemId: defectiveInId(id),
        },
        {
          sectionId: id,
          itemId: commentId(id),
        },
      );

      dependencies[defectiveInId(id)].push({
        sectionId: id,
        itemId: commentId(id),
      });
    }
  });

  return dependencies;
};

const mergeValues = (assessment, items) => {
  const updatedAssessment = JSON.parse(JSON.stringify(assessment));

  items.forEach((item) => {
    const id = item.itemType;
    const section = updatedAssessment.sections[id];

    if (!section) {
      return;
    }

    if (id in v2.SECTION) {
      updatedAssessment.sections[id] = persisted.toDraftAssessmentSection(
        draft,
        item,
      );

      return;
    }

    const acceptedRooms = Object.values(DEFECTIVE_IN_TYPES);

    const isWorkingValue = item?.itemFunctional?.isFunctional || '';

    const itemComment = item?.itemFunctional?.comment || '';
    const defectiveInValue = splitComment(itemComment).filter((room) =>
      acceptedRooms.includes(room),
    );
    const commentValue = splitComment(itemComment)
      .filter((room) => !acceptedRooms.includes(room))
      .join(', ');

    const conservationValue = item?.itemStatus?.conservation || '';

    const isWorking = section.items[isWorkingId(id)];
    const defectiveIn = section.items[defectiveInId(id)];
    const comment = section.items[commentId(id)];
    const conservation = section.items[conservationId(id)];

    const groupId = GROUP_BY_SECTION[id];
    const groupSection = updatedAssessment.sections[groupId];
    const groupIsWorking = groupSection.items[isWorkingId(groupId)];

    if (groupIsWorking?.value === '' && isWorking) {
      groupIsWorking.value = isWorkingValue
        ? IS_WORKING_TYPES.YES
        : IS_WORKING_TYPES.NO;
    }

    if (isWorking) {
      isWorking.value = isWorkingValue;
    }

    if (defectiveIn) {
      defectiveIn.value =
        commentValue.length > 0
          ? defectiveInValue.concat(DEFECTIVE_IN_TYPES.OTHER)
          : defectiveInValue;
    }

    if (comment) {
      comment.value = commentValue;
    }

    if (conservation) {
      conservation.value = conservationValue;
    }
  });

  return updatedAssessment;
};

const getGeneralRoomItems = (payload) => {
  const rooms = payload?.rooms || [];
  const generalRoom = rooms.find(({ room }) => room === 'GENERAL');
  return generalRoom?.items || [];
};

const getStatus = (payload) => {
  if (payload?.completed) {
    return STATUS.COMPLETED;
  }

  const items = getGeneralRoomItems(payload);

  if (items.length > 0) {
    return STATUS.STARTED;
  }

  return STATUS.NOT_STARTED;
};

export const convertToAssessment = (payload) => {
  const items = getGeneralRoomItems(payload);

  const assessment = {
    status: getStatus(payload),
    sections: buildSections(),
    dependencies: buildDependencies(),
  };

  return mergeValues(assessment, items);
};

const isValidValue = (value) => value?.length > 0;

const convertToItem = (section) => {
  if (section.id in v2.SECTION) {
    return draft.toPersistedAssessmentItem(section);
  }

  const working = section.items[isWorkingId(section.id)];
  const defectiveIn = section.items[defectiveInId(section.id)];
  const comment = section.items[commentId(section.id)];
  const conservation = section.items[conservationId(section.id)];

  const item = {
    itemGroup: GROUP_BY_SECTION[section.id],
    itemType: section.id,
    present: true,
  };

  if (isValidValue(working?.value)) {
    item.itemFunctional = {};
    item.itemFunctional.isFunctional = working.value;
  }

  if (isValidValue(defectiveIn?.value)) {
    item.itemFunctional = item.itemFunctional || {};
    item.itemFunctional.comment = defectiveIn.value
      .filter((v) => v !== DEFECTIVE_IN_TYPES.OTHER)
      .join(',')
      .replace(/\s+,\s+/g, ',');
  }

  if (isValidValue(comment?.value)) {
    item.itemFunctional = item.itemFunctional || {};

    const rooms = comment.value.split(',');
    const joinedComment = [item.itemFunctional.comment, ...rooms]
      .filter((v) => v.trim().length > 1)
      .map((v) =>
        v
          .trim()
          .replace(/\s*,\s*/g, ',')
          .replace(/,+/g, ','),
      )
      .join(',')
      .replace(/,+/g, ',');

    item.itemFunctional.comment = joinedComment;
  }

  if (isValidValue(conservation?.value)) {
    item.itemStatus = item.itemStatus || {};
    item.itemStatus.conservation = conservation.value;
  }

  if (v2.isEnabled()) {
    return PersistedAssessment.attachV2Properties(item);
  }

  return item;
};

export const convertFromAssessment = (assessment) =>
  Object.values(assessment.sections)
    .filter((s) => !NON_SAVEABLE_SECTIONS.includes(s.id))
    .map(convertToItem);

export const createSectionVisibilityMap = ({ checklist, assessment }) => {
  const bathroomItems = checklist?.house?.bathroom?.items;
  const bedroomItems = checklist?.house?.bedroom?.items;
  const kitchenItems = checklist?.house?.kitchen?.items;

  const visibilityMap = {
    [SECTIONS.BATHROOM_STALL]: bathroomItems?.BOX?.value === true,
    [SECTIONS.BATHROOM_CABINET]:
      bathroomItems?.ARMARIOS_NOS_BANHEIROS?.value === true,
    [SECTIONS.BEDROOM_CLOSET]:
      bedroomItems?.ARMARIOS_EMBUTIDOS_NO_QUARTO?.value === true,
    [SECTIONS.KITCHEN_CABINET]:
      kitchenItems?.ARMARIOS_NA_COZINHA?.value === true,
  };

  visibilityMap[SECTIONS.PROPERTY_ITEMS] =
    visibilityMap[SECTIONS.BATHROOM_STALL] ||
    visibilityMap[SECTIONS.BATHROOM_CABINET] ||
    visibilityMap[SECTIONS.BEDROOM_CLOSET] ||
    visibilityMap[SECTIONS.KITCHEN_CABINET];

  if (isIntercomEnabled()) {
    const fixedHouseItems = checklist?.fixed?.house?.items;

    visibilityMap[SECTIONS.INTERCOM] = fixedHouseItems?.tipo?.value !== 'house';

    visibilityMap[SECTIONS.PROPERTY_ITEMS] =
      visibilityMap[SECTIONS.PROPERTY_ITEMS] ||
      visibilityMap[SECTIONS.INTERCOM];
  }

  if (v2.isEnabled()) {
    const gasSupplyType = findAssessmentItem(
      assessment,
      v2.QUESTION.GAS_SUPPLY_TYPE,
    );

    const gasRegisterLocation = findAssessmentItem(
      assessment,
      v2.QUESTION.GAS_REGISTER_LOCATION,
    );

    const showGasRegisterSections =
      gasSupplyType?.value === v2.ANSWER.PIPED_GAS &&
      gasRegisterLocation?.value === v2.ANSWER.YES;

    visibilityMap[SECTIONS.GAS_REGISTER_1] = showGasRegisterSections;
    visibilityMap[SECTIONS.GAS_REGISTER_2] = showGasRegisterSections;

    const waterHeaterInstalled = findAssessmentItem(
      assessment,
      v2.QUESTION.WATER_HEATER_INSTALLED,
    );

    const showWaterHeater1Section =
      bathroomItems?.CHUVEIRO_A_GAS?.value === true;

    const showWaterHeater2Section =
      bathroomItems?.CHUVEIRO_A_GAS?.value === true &&
      waterHeaterInstalled?.value === v2.ANSWER.YES;

    visibilityMap[SECTIONS.WATER_HEATER_1] = showWaterHeater1Section;
    visibilityMap[SECTIONS.WATER_HEATER_2] = showWaterHeater2Section;
  }

  // assessment v2
  ASSESSMENT_V2_SECTIONS.forEach((section) => {
    if (section in visibilityMap) {
      visibilityMap[section] = assessment?.isV2Enabled
        ? visibilityMap[section]
        : false;
    } else {
      visibilityMap[section] = assessment?.isV2Enabled ?? false;
    }
  });

  return visibilityMap;
};
